import React, { useState, useEffect } from "react";
import "./AggregatedOrderDetails.css";

import { connect } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import * as actions from "../../store/actions";
import Spinner from "../../containers/UI/Spinner/Spinner";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// ==============================
// STATUS DISPLAY HELPERS
// ==============================

const STATUS_LABELS = {
  PENDING_CONFIRMATION: "Pending Confirmation",
  CONFIRMED: "Confirmed",
  COLLECTED: "Collected",
  CANCELLED: "Cancelled",
};

const STATUS_COLOR_CLASS = {
  PENDING_CONFIRMATION: "aggDetail--pending",
  CONFIRMED: "aggDetail--confirmed",
  COLLECTED: "aggDetail--collected",
  CANCELLED: "aggDetail--cancelled",
};

const REJECTION_REASONS = [
  { value: "out_of_stock", label: "Out of Stock" },
  { value: "wrong_price",  label: "Wrong Price" },
  { value: "other",        label: "Other" },
];

/**
 * Format an ISO date string to DD.MM.YYYY HH:MM, or return '—' if null/undefined.
 */
const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ==============================
// COMPONENT
// ==============================

const AggregatedOrderDetails = (props) => {
  const { aggOrderId } = useParams();
  const navigate = useNavigate();

  const {
    aggOrderLoading,
    aggOrderDetails,
    aggConfirmLoading,
    itemUpdateLoading,
    getOneAggregatedOrder,
    confirmAggregatedOrder,
    updateAggregatedOrderItem,
  } = props;

  // ── Local state ──
  // Map of itemId → confirmed quantity (editable number)
  const [confirmedQtys, setConfirmedQtys] = useState({});
  // Rejection popup state
  const [rejectModal, setRejectModal] = useState({ open: false, item: null });
  const [selectedReason, setSelectedReason] = useState("");
  // Tracks the confirm-all flow (saving items + confirming)
  const [isConfirming, setIsConfirming] = useState(false);
  // Tracks per-item rejection saving
  const [rejectSaving, setRejectSaving] = useState(false);

  const loadOrder = () => {
    getOneAggregatedOrder(aggOrderId);
  };

  useEffect(() => {
    loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggOrderId]);

  // Initialise confirmedQtys whenever order details (re-)load
  useEffect(() => {
    if (aggOrderDetails?.items) {
      const qtys = {};
      aggOrderDetails.items.forEach((item) => {
        // Pre-fill from existing confirmedQuantity, fall back to requestedQuantity
        qtys[item.id] = item.confirmedQuantity ?? item.requestedQuantity;
      });
      setConfirmedQtys(qtys);
    }
  }, [aggOrderDetails]);

  // ── Quantity change handler ──
  const handleQtyChange = (itemId, value) => {
    const clamped = Math.max(0, Number(value) || 0);
    setConfirmedQtys((prev) => ({ ...prev, [itemId]: clamped }));
  };

  // ── Rejection modal handlers ──
  const handleRejectItem = (item) => {
    setRejectModal({ open: true, item });
    setSelectedReason("");
  };

  const handleRejectCancel = () => {
    setRejectModal({ open: false, item: null });
    setSelectedReason("");
  };

  const handleRejectSubmit = async () => {
    if (!selectedReason || !rejectModal.item) return;

    setRejectSaving(true);
    await updateAggregatedOrderItem(
      aggOrderDetails.id,
      rejectModal.item.id,
      { rejectionReason: selectedReason }
    );
    setRejectSaving(false);
    setRejectModal({ open: false, item: null });
    setSelectedReason("");
    loadOrder(); // refresh to show updated state
  };

  // ── Confirm Order handler ──
  const handleConfirm = async () => {
    if (!aggOrderDetails) return;

    setIsConfirming(true);

    const nonRejectedItems = (aggOrderDetails.items || []).filter(
      (item) => !item.rejectionReason
    );

    // Save confirmed quantity for every non-rejected item in parallel
    await Promise.all(
      nonRejectedItems.map((item) =>
        updateAggregatedOrderItem(
          aggOrderDetails.id,
          item.id,
          { confirmedQuantity: Number(confirmedQtys[item.id] ?? item.requestedQuantity) }
        )
      )
    );

    // Then confirm the order
    confirmAggregatedOrder(aggOrderDetails.id, () => {
      setIsConfirming(false);
      loadOrder();
    });
  };

  // ── Loading state ──
  if (aggOrderLoading) {
    return (
      <div className="aggDetailRoot">
        <Spinner />
      </div>
    );
  }

  // ── Not found state ──
  if (!aggOrderDetails) {
    return (
      <div className="aggDetailRoot">
        <div className="aggDetailNotFound">
          <span>Order not found.</span>
          <div
            className="aggDetailBackButton"
            onClick={() => navigate("/orders")}
          >
            <ArrowBackIcon fontSize="small" />
            <span>Back to Orders</span>
          </div>
        </div>
      </div>
    );
  }

  const {
    aggregatedOrderNumber,
    status,
    cutoffDate,
    confirmedAt,
    totalAmount,
    itemCount,
    createdAt,
    items = [],
    merchant,
  } = aggOrderDetails;

  const statusLabel = STATUS_LABELS[status] || status;
  const statusClass = STATUS_COLOR_CLASS[status] || "";
  const isPendingConfirmation = status === "PENDING_CONFIRMATION";
  const isActionLoading = isConfirming || aggConfirmLoading;

  return (
    <div className="aggDetailRoot">

      {/* ── Rejection Reason Modal ── */}
      {rejectModal.open && (
        <div className="aggRejectModal">
          <div
            className="aggRejectModal__backdrop"
            onClick={handleRejectCancel}
          />
          <div className="aggRejectModal__card">
            <div className="aggRejectModal__title">
              Reject Item
            </div>
            <div className="aggRejectModal__itemName">
              {rejectModal.item?.productName}
            </div>
            <div className="aggRejectModal__body">
              <span className="aggRejectModal__label">
                Select a reason:
              </span>
              {REJECTION_REASONS.map((reason) => (
                <label key={reason.value} className="aggRejectModal__option">
                  <input
                    type="radio"
                    name="rejectReason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={() => setSelectedReason(reason.value)}
                  />
                  <span>{reason.label}</span>
                </label>
              ))}
            </div>
            <div className="aggRejectModal__actions">
              <div
                className="aggRejectModal__cancelBtn"
                onClick={handleRejectCancel}
              >
                Cancel
              </div>
              {rejectSaving ? (
                <div className="aggRejectModal__savingSpinner">
                  <Spinner />
                </div>
              ) : (
                <div
                  className={`aggRejectModal__confirmBtn ${!selectedReason ? "aggRejectModal__confirmBtn--disabled" : ""}`}
                  onClick={selectedReason ? handleRejectSubmit : undefined}
                >
                  Confirm Rejection
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className="aggDetailHeader">
        <div
          className="aggDetailBackButton"
          onClick={() => navigate("/orders")}
        >
          <ArrowBackIcon fontSize="small" />
          <span>Back to Orders</span>
        </div>
        <div className="aggDetailTitle">
          Aggregated Order: {aggregatedOrderNumber}
        </div>
      </div>

      {/* ── Order Summary Card ── */}
      <div className={`aggDetailCard ${statusClass}`}>
        <div className="aggDetailMeta">
          <div className="aggDetailMetaRow">
            <span className="aggDetailMetaLabel">Status</span>
            <span className={`aggDetailStatusBadge ${statusClass}`}>
              {statusLabel}
            </span>
          </div>
          <div className="aggDetailMetaRow">
            <span className="aggDetailMetaLabel">Merchant</span>
            <span>{merchant?.name || "—"}</span>
          </div>
          <div className="aggDetailMetaRow">
            <span className="aggDetailMetaLabel">Total Items</span>
            <span>{itemCount}</span>
          </div>
          <div className="aggDetailMetaRow">
            <span className="aggDetailMetaLabel">Total Amount</span>
            <span className="aggDetailTotalAmount">
              QAR {Number(totalAmount).toFixed(2)}
            </span>
          </div>
          <div className="aggDetailMetaRow">
            <span className="aggDetailMetaLabel">Cutoff Date</span>
            <span>{formatDate(cutoffDate)}</span>
          </div>
          {isPendingConfirmation && (
            <div className="aggDetailMetaRow aggDetailDeadlineRow">
              <span className="aggDetailMetaLabel">Confirmation Deadline</span>
              <span className="aggDetailDeadline">{formatDate(cutoffDate)}</span>
            </div>
          )}
          {confirmedAt && (
            <div className="aggDetailMetaRow">
              <span className="aggDetailMetaLabel">Confirmed At</span>
              <span>{formatDate(confirmedAt)}</span>
            </div>
          )}
          <div className="aggDetailMetaRow">
            <span className="aggDetailMetaLabel">Created</span>
            <span>{formatDate(createdAt)}</span>
          </div>
        </div>

        {/* ── Confirm Order Button ── */}
        {isPendingConfirmation && (
          <div className="aggDetailActions">
            {isActionLoading ? (
              <div className="aggDetailConfirmLoading">
                <Spinner />
              </div>
            ) : (
              <div className="aggDetailConfirmButton" onClick={handleConfirm}>
                Confirm Order
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Items Table ── */}
      <div className="aggDetailItemsSection">
        <div className="aggDetailSectionTitle">
          Order Items
          {isPendingConfirmation && (
            <span className="aggDetailSectionHint">
              &nbsp;— set confirmed quantities and reject items as needed before confirming
            </span>
          )}
        </div>
        {items.length === 0 ? (
          <div className="aggDetailNoItems">No items found.</div>
        ) : (
          <div className="aggDetailTableScroll">
            <table className="aggDetailItemsTable">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Unit</th>
                  <th>Unit Price</th>
                  <th>Requested Qty</th>
                  <th>Confirmed Qty</th>
                  {isPendingConfirmation && <th>Actions</th>}
                  {!isPendingConfirmation && <th>Rejection Reason</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const isRejected = Boolean(item.rejectionReason);
                  return (
                    <tr
                      key={item.id}
                      className={isRejected ? "aggItemRow--rejected" : ""}
                    >
                      <td className="aggItemName">{item.productName}</td>
                      <td>{item.product?.unit || "—"}</td>
                      <td>QAR {Number(item.unitPrice).toFixed(2)}</td>
                      <td>{item.requestedQuantity}</td>

                      {/* Confirmed Qty — editable when PENDING_CONFIRMATION and not rejected */}
                      <td>
                        {isPendingConfirmation && !isRejected ? (
                          <input
                            type="number"
                            className="aggItemQtyInput"
                            min="0"
                            value={confirmedQtys[item.id] ?? item.requestedQuantity}
                            onChange={(e) => handleQtyChange(item.id, e.target.value)}
                          />
                        ) : (
                          <span>
                            {item.confirmedQuantity ?? "—"}
                          </span>
                        )}
                      </td>

                      {/* Actions column (PENDING only) */}
                      {isPendingConfirmation && (
                        <td>
                          {isRejected ? (
                            <span className="aggItemRejectedTag">
                              Rejected: {item.rejectionReason.replace("_", " ")}
                            </span>
                          ) : (
                            <div
                              className="aggItemRejectButton"
                              onClick={() => handleRejectItem(item)}
                            >
                              Reject
                            </div>
                          )}
                        </td>
                      )}

                      {/* Rejection Reason column (read-only statuses) */}
                      {!isPendingConfirmation && (
                        <td className="aggItemRejectionReason">
                          {item.rejectionReason
                            ? item.rejectionReason.replace("_", " ")
                            : "—"}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  aggOrderLoading: state.orders.aggOrderLoading,
  aggOrderDetails: state.orders.aggOrderDetails,
  aggConfirmLoading: state.orders.aggConfirmLoading,
  itemUpdateLoading: state.orders.itemUpdateLoading,
});

const mapDispatchToProps = (dispatch) => ({
  getOneAggregatedOrder: (id) => dispatch(actions.getOneAggregatedOrder(id)),
  confirmAggregatedOrder: (id, cb) =>
    dispatch(actions.confirmAggregatedOrder(id, cb)),
  updateAggregatedOrderItem: (aggOrderId, itemId, payload, cb) =>
    dispatch(actions.updateAggregatedOrderItem(aggOrderId, itemId, payload, cb)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AggregatedOrderDetails);
